/**
 * Nivo theme configuration using the existing design system
 * Colors are pulled from CSS custom properties to stay in sync with shared-design/theme.css
 */

/**
 * Get color from CSS custom properties
 * @param {string} colorVar - CSS variable name (e.g., '--chart-color-1')
 * @returns {string} Color value
 */
const getThemeColor = (colorVar) => {
  return getComputedStyle(document.documentElement).getPropertyValue(colorVar).trim();
};

/**
 * Get the chart color palette from the design system
 * @returns {Array<string>} Array of colors
 */
export const getChartColors = () => {
  return [
    getThemeColor('--chart-color-1'),   // Orange dark (Primary)
    getThemeColor('--chart-color-2'),   // Blue darkest
    getThemeColor('--chart-color-3'),   // Orange medium
    getThemeColor('--chart-color-4'),   // Blue dark
    getThemeColor('--chart-color-5'),   // Orange light
    getThemeColor('--chart-color-6'),   // Blue light
    getThemeColor('--chart-color-7'),   // Orange darkest
    getThemeColor('--chart-color-8'),   // Blue lightest
    getThemeColor('--chart-color-9'),   // Green (extended palette)
    getThemeColor('--chart-color-10'),  // Purple (extended palette)
    getThemeColor('--chart-color-11'),  // Pink (extended palette)
    getThemeColor('--chart-color-12')   // Teal (extended palette)
  ];
};

/**
 * Get design system value from CSS variables
 * @param {string} varName - CSS variable name
 * @returns {string} CSS variable value
 */
const getDesignSystemValue = (varName) => {
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
};

/**
 * Base Nivo theme for all charts
 * Uses design system CSS variables for consistency
 */
export const getNivoTheme = () => ({
  background: 'transparent',
  textColor: getDesignSystemValue('--color-text-primary'),
  fontSize: 12, // Nivo requires number, matches --font-size-sm (0.75rem = 12px)
  fontFamily: getDesignSystemValue('--font-primary'),
  axis: {
    domain: {
      line: {
        stroke: getDesignSystemValue('--color-border-light'),
        strokeWidth: 1
      }
    },
    ticks: {
      line: {
        stroke: getDesignSystemValue('--color-border-light'),
        strokeWidth: 1
      },
      text: {
        fontSize: 9, // Smaller for rotated X-axis labels (reduced from 12)
        fill: getDesignSystemValue('--color-text-primary'),
        fontFamily: getDesignSystemValue('--font-primary'),
        outlineWidth: 0
      }
    },
    legend: {
      text: {
        fontSize: 12, // --font-size-sm
        fill: getDesignSystemValue('--color-text-primary'),
        fontFamily: getDesignSystemValue('--font-primary'),
        fontWeight: 500 // --font-weight-medium
      }
    }
  },
  grid: {
    line: {
      stroke: getDesignSystemValue('--color-border-light'),
      strokeWidth: 1
    }
  },
  legends: {
    text: {
      fontSize: 9, // Smaller font for compact legends
      fill: getDesignSystemValue('--color-text-secondary'),
      fontFamily: getDesignSystemValue('--font-primary')
    }
  },
  labels: {
    text: {
      fontSize: 12, // --font-size-sm
      fill: getDesignSystemValue('--color-text-primary'),
      fontFamily: getDesignSystemValue('--font-primary')
    }
  },
  dots: {
    text: {
      fontSize: 12, // --font-size-sm
      fill: getDesignSystemValue('--color-text-primary'),
      fontFamily: getDesignSystemValue('--font-primary')
    }
  },
  tooltip: {
    container: {
      background: 'rgba(255, 255, 255, 0.95)',
      color: getDesignSystemValue('--color-text-primary'),
      fontSize: 11, // Smaller for compact tooltips (between xs and sm)
      fontFamily: getDesignSystemValue('--font-primary'),
      borderRadius: getDesignSystemValue('--radius-sm'),
      boxShadow: getDesignSystemValue('--shadow-md'),
      padding: `${getDesignSystemValue('--spacing-3')} ${getDesignSystemValue('--spacing-4')}`, // 6px 8px
      border: `1px solid ${getDesignSystemValue('--color-border-light')}`
    },
    basic: {
      whiteSpace: 'pre-wrap',
      display: 'flex',
      alignItems: 'center'
    },
    chip: {
      marginRight: getDesignSystemValue('--spacing-4') // 8px
    },
    table: {},
    tableCell: {
      padding: `${getDesignSystemValue('--spacing-2')} ${getDesignSystemValue('--spacing-3')}` // 4px 6px
    }
  }
});

/**
 * Common chart props with theme and colors
 * @returns {Object} Common Nivo props
 */
export const getNivoCommonProps = () => ({
  theme: getNivoTheme(),
  colors: getChartColors(),
  animate: true,
  motionConfig: 'gentle'
});
